/**
 * Breach Detection Component
 * ISO 27001 A.9.4.3 - Password management system
 *
 * Uses Have I Been Pwned API to check:
 * - Password exposure (k-anonymity - password never sent)
 * - Email breaches (requires API key)
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ShieldAlert,
  ShieldCheck,
  Mail,
  Key,
  Search,
  CheckCircle,
  XCircle,
  ExternalLink,
  Eye,
  EyeOff,
  Info,
} from 'lucide-react';
import { Button, Spinner } from '@/components/ui-v2';
import {
  useBreachDetectionStatus,
  useCheckPasswordBreach,
  useCheckEmailBreach,
} from '../hooks/useSecuritySettings';
import { showWarning } from '@/lib/notifications';

export const BreachCheck: React.FC = () => {
  const { t } = useTranslation();
  const { data: status, isLoading: statusLoading } = useBreachDetectionStatus();
  const passwordBreachMutation = useCheckPasswordBreach();
  const emailBreachMutation = useCheckEmailBreach();

  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordResult, setPasswordResult] = useState<{
    compromised: boolean;
    exposureCount: number;
    message?: string;
  } | null>(null);

  const [emailResult, setEmailResult] = useState<{
    breached: boolean;
    breachCount: number;
    breaches: Array<{
      name: string;
      title?: string;
      breachDate: string;
      pwnCount?: number;
    }>;
  } | null>(null);

  const handleCheckPassword = async () => {
    if (!password.trim()) return;
    try {
      const result = await passwordBreachMutation.mutateAsync(password);
      setPasswordResult(result);
    } catch (err) {
      setPasswordResult(null);
    }
  };

  const handleCheckEmail = async () => {
    try {
      const result = await emailBreachMutation.mutateAsync();
      setEmailResult(result);
    } catch (err: any) {
      if (err.message?.includes('503')) {
        showWarning(
          t('admin.breachCheck.hibpServiceUnavailable'),
          t('admin.breachCheck.hibpServiceUnavailableTitle')
        );
      }
      setEmailResult(null);
    }
  };

  const cardClasses = `bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10
    border border-white/30 p-6 hover:bg-white/70 hover:shadow-2xl
    transition-all duration-300`;

  const inputClasses = `w-full px-4 py-3 bg-white/40 border border-white/50 rounded-2xl
    focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white
    hover:bg-white hover:border-teal-400 hover:shadow-lg hover:shadow-teal-100/50
    transition-all duration-300 outline-none text-sm`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-amber-100 rounded-2xl">
            <ShieldAlert className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">{t('admin.breachCheck.title')}</h2>
            <p className="text-sm text-gray-500">{t('admin.breachCheck.subtitle')}</p>
          </div>
        </div>
      </div>

      {/* Status Banner */}
      {status && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-4 border border-blue-200">
          <div className="flex items-center gap-2 mb-3">
            <Info className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-700">{t('admin.breachCheck.serviceStatus')}</span>
          </div>
          <div className="flex flex-wrap gap-3 text-xs">
            <div className="flex items-center gap-2 bg-white/50 rounded-xl px-3 py-2">
              {status.passwordChecksEnabled ? (
                <CheckCircle className="w-4 h-4 text-green-500" />
              ) : (
                <XCircle className="w-4 h-4 text-red-500" />
              )}
              <span>{t('admin.breachCheck.passwordChecks')}</span>
            </div>
            <div className="flex items-center gap-2 bg-white/50 rounded-xl px-3 py-2">
              {status.emailChecksEnabled ? (
                <CheckCircle className="w-4 h-4 text-green-500" />
              ) : (
                <XCircle className="w-4 h-4 text-gray-400" />
              )}
              <span>{t('admin.breachCheck.emailChecks')} {!status.emailChecksEnabled && t('admin.breachCheck.emailChecksApiKeyRequired')}</span>
            </div>
            <a
              href={status.documentation}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-blue-600 hover:underline"
            >
              <ExternalLink className="w-3 h-3" />
              {t('admin.breachCheck.documentation')}
            </a>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Password Check */}
        <div className={cardClasses}>
          <div className="flex items-center gap-3 mb-4">
            <Key className="w-5 h-5 text-teal-600" />
            <h3 className="font-semibold text-gray-900">{t('admin.breachCheck.checkPassword')}</h3>
          </div>
          <p className="text-xs text-gray-500 mb-4">
            {t('admin.breachCheck.passwordKAnonymity')}
          </p>
          <div className="space-y-4">
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setPasswordResult(null);
                }}
                placeholder={t('admin.breachCheck.passwordPlaceholder')}
                className={inputClasses}
                onKeyDown={(e) => e.key === 'Enter' && handleCheckPassword()}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <Button
              onClick={handleCheckPassword}
              disabled={!password.trim() || passwordBreachMutation.isPending}
              className="w-full flex items-center justify-center gap-2"
            >
              {passwordBreachMutation.isPending ? (
                <Spinner className="w-4 h-4" />
              ) : (
                <Search className="w-4 h-4" />
              )}
              {t('admin.breachCheck.verifyPassword')}
            </Button>

            {/* Password Result */}
            {passwordResult && (
              <div
                className={`p-4 rounded-2xl ${
                  passwordResult.compromised
                    ? 'bg-red-50 border border-red-200'
                    : 'bg-green-50 border border-green-200'
                }`}
              >
                <div className="flex items-center gap-3">
                  {passwordResult.compromised ? (
                    <XCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                  ) : (
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                  )}
                  <div>
                    <p
                      className={`font-medium ${
                        passwordResult.compromised ? 'text-red-700' : 'text-green-700'
                      }`}
                    >
                      {passwordResult.compromised
                        ? t('admin.breachCheck.passwordCompromised')
                        : t('admin.breachCheck.passwordSafe')}
                    </p>
                    {passwordResult.compromised && (
                      <p className="text-xs text-red-600 mt-1">
                        {t('admin.breachCheck.passwordFoundCount', { count: passwordResult.exposureCount.toLocaleString() })}
                      </p>
                    )}
                  </div>
                </div>
                {passwordResult.message && (
                  <p className="text-xs text-gray-600 mt-2">{passwordResult.message}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Email Check */}
        <div className={cardClasses}>
          <div className="flex items-center gap-3 mb-4">
            <Mail className="w-5 h-5 text-teal-600" />
            <h3 className="font-semibold text-gray-900">{t('admin.breachCheck.checkEmail')}</h3>
          </div>
          <p className="text-xs text-gray-500 mb-4">
            {t('admin.breachCheck.emailDescription')}
            {!status?.emailChecksEnabled && (
              <span className="text-amber-600 block mt-1">
                {t('admin.breachCheck.emailServiceUnavailable')}
              </span>
            )}
          </p>
          <div className="space-y-4">
            <Button
              onClick={handleCheckEmail}
              disabled={emailBreachMutation.isPending || !status?.emailChecksEnabled}
              variant={status?.emailChecksEnabled ? 'primary' : 'secondary'}
              className="w-full flex items-center justify-center gap-2"
            >
              {emailBreachMutation.isPending ? (
                <Spinner className="w-4 h-4" />
              ) : (
                <Search className="w-4 h-4" />
              )}
              {t('admin.breachCheck.verifyEmail')}
            </Button>

            {/* Email Result */}
            {emailResult && (
              <div
                className={`p-4 rounded-2xl ${
                  emailResult.breached
                    ? 'bg-red-50 border border-red-200'
                    : 'bg-green-50 border border-green-200'
                }`}
              >
                <div className="flex items-center gap-3">
                  {emailResult.breached ? (
                    <XCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                  ) : (
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                  )}
                  <div>
                    <p
                      className={`font-medium ${
                        emailResult.breached ? 'text-red-700' : 'text-green-700'
                      }`}
                    >
                      {emailResult.breached
                        ? t('admin.breachCheck.emailBreached', { count: emailResult.breachCount })
                        : t('admin.breachCheck.emailSafe')}
                    </p>
                  </div>
                </div>

                {/* Breach List */}
                {emailResult.breached && emailResult.breaches.length > 0 && (
                  <div className="mt-4 space-y-2 max-h-48 overflow-y-auto">
                    {emailResult.breaches.map((breach, index) => (
                      <div
                        key={index}
                        className="bg-white/60 rounded-xl p-3 text-xs"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-gray-800">
                              {breach.title || breach.name}
                            </p>
                            <p className="text-gray-500">
                              {t('admin.breachCheck.breachDate')} {new Date(breach.breachDate).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                          {breach.pwnCount && (
                            <span className="text-red-600 font-mono">
                              {(breach.pwnCount / 1000000).toFixed(1)}M {t('admin.breachCheck.accounts')}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-2xl p-4 border border-gray-200">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-gray-600">
            <p className="font-medium text-gray-800 mb-1">{t('admin.breachCheck.aboutTitle')}</p>
            <p>
              {t('admin.breachCheck.aboutDescription')}
            </p>
            <a
              href="https://haveibeenpwned.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-teal-600 hover:underline mt-2"
            >
              {t('admin.breachCheck.learnMore')}
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BreachCheck;
